import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';

interface BlogStickyPopupProps {
  isVisible: boolean;
  onClose: () => void;
}

export function BlogStickyPopup({ isVisible: _isVisible, onClose: _onClose }: BlogStickyPopupProps) {
  return null;

  const navigate = useNavigate();

  const handleSignUp = () => {
    navigate('/onboarding?mode=register&source=blog-popup');
  };

  const handleLearnMore = () => {
    // Scroll to the inline BlogAcquisitionCTA
    const ctaElement = document.querySelector('[data-blog-acquisition-cta]');
    if (ctaElement) {
      ctaElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      // Fallback to pricing page if CTA not found
      navigate('/#pricing');
    }
    onClose(); // Close popup after action
  };

  const handleClose = () => {
    onClose();
    // Store dismissal timestamp in localStorage
    localStorage.setItem('rulevia_blog_popup_dismissed', Date.now().toString());
  };

  // Handle ESC key
  React.useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isVisible) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isVisible]);

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Mobile Version */}
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-50 p-4 md:hidden"
          >
            <div className="bg-gradient-to-br from-purple-900/95 to-blue-900/95 backdrop-blur-xl rounded-t-2xl border border-purple-500/30 border-b-0 p-4 shadow-2xl">
              {/* Close Button */}
              <button
                onClick={handleClose}
                className="absolute top-3 right-3 p-1 text-gray-400 hover:text-white transition-colors"
                aria-label="Close popup"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Content */}
              <div className="pr-8">
                <h3 className="text-xl font-bold text-white mb-2">
                  Try Rulevia
                </h3>
                <p className="text-sm text-gray-200 mb-4 leading-relaxed">
                  AI-powered ad compliance & performance analysis. Start with 3 free credits—no credit card required.
                </p>

                {/* Buttons */}
                <div className="flex flex-col gap-2">
                  <button
                    onClick={handleSignUp}
                    className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all transform hover:scale-105 shadow-lg"
                  >
                    Sign up
                  </button>
                  <button
                    onClick={handleLearnMore}
                    className="w-full border-2 border-white/30 text-white py-3 px-6 rounded-lg font-semibold hover:bg-white/10 hover:border-white/50 transition-all"
                  >
                    Learn more
                  </button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Desktop Version */}
          <motion.div
            initial={{ x: 100, y: 50, opacity: 0, scale: 0.9 }}
            animate={{ x: 0, y: 0, opacity: 1, scale: 1 }}
            exit={{ x: 100, y: 50, opacity: 0, scale: 0.9 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-8 right-8 z-50 hidden md:block"
          >
            <div className="bg-gradient-to-br from-purple-900/95 to-blue-900/95 backdrop-blur-xl rounded-2xl border border-purple-500/30 p-6 shadow-2xl hover:shadow-purple-500/25 transition-shadow w-96 max-w-[400px]">
              {/* Close Button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 p-1 text-gray-400 hover:text-white transition-colors"
                aria-label="Close popup"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Content */}
              <div className="pr-8">
                <h3 className="text-2xl font-bold text-white mb-3">
                  Try Rulevia
                </h3>
                <p className="text-sm text-gray-200 mb-6 leading-relaxed">
                  AI-powered ad compliance & performance analysis. Start with 3 free credits—no credit card required.
                </p>

                {/* Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={handleSignUp}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all transform hover:scale-105 shadow-lg"
                  >
                    Sign up
                  </button>
                  <button
                    onClick={handleLearnMore}
                    className="flex-1 border-2 border-white/30 text-white py-3 px-4 rounded-lg font-semibold hover:bg-white/10 hover:border-white/50 transition-all"
                  >
                    Learn more
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}